import { useEffect, useRef, useState } from 'react';
import { useRecorder } from '../lib/audio/record.js';
import { formatDuration } from '../lib/audio/prepare.js';

interface Props {
  /** Called once, the moment a recording is stopped and ready to prepare. */
  onRecorded: (file: File) => void;
  disabled?: boolean;
  /** The animal's name — every prompt addresses them, not "the recording". */
  subjectLabel: string;
  /** "Bindi's 4th recording · +10 credits when it lands" — stated, never demanded. */
  standingLabel?: string | undefined;
  /** The idle card's own "Upload a file" link — replaces the old mode-tabs toggle. */
  onSwitchToFile: () => void;
}

/** Below this, the take-live-hint reads as quiet rather than silent. */
const QUIET_DBFS = -42;
/** A frame effectively pinned at full scale — the byte-domain data tops out at 127/128. */
const CLIP_AMPLITUDE = 0.98;

/**
 * Records straight from the microphone, as an alternative to choosing a file.
 *
 * Deliberately thin: it owns the button, the timer and the live level meter,
 * and nothing else. Once a recording stops it hands the file to the parent and
 * gets out of the way — review, the real spectrogram, and the upload progress
 * are the existing "chosen file" view in ContributePage, unchanged. A recorded
 * clip and an uploaded one look identical from that point on, because they go
 * through the same `prepareAudio` call.
 *
 * The "lively" skin: warm rather than clinical, because this is the moment
 * the app should feel like it's listening to an animal, not reading an
 * oscilloscope. The measurement underneath is unchanged — same analyser, same
 * level meter — only the surface differs. dBFS, peak and clipping are demoted
 * behind a closed-by-default disclosure rather than removed, and the coaching
 * line under the meter is derived from the same numbers so a contributor gets
 * something they can act on instead of a reading they can't.
 */
export function RecordPanel({ onRecorded, disabled, subjectLabel, standingLabel, onSwitchToFile }: Props) {
  const { status, elapsedMs, error, analyser, file, start, stop } = useRecorder();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hintRef = useRef<HTMLParagraphElement>(null);
  const levelValueRef = useRef<HTMLSpanElement>(null);
  const peakValueRef = useRef<HTMLSpanElement>(null);
  const clipValueRef = useRef<HTMLSpanElement>(null);
  const handedOff = useRef(false);
  const [levelsOpen, setLevelsOpen] = useState(false);

  // Peak dBFS and "did this take ever clip" both persist for the whole take,
  // not just the current frame — reset the moment a new recording starts.
  const peakDbfsRef = useRef(-Infinity);
  const everClippedRef = useRef(false);

  useEffect(() => {
    if (status === 'stopped' && file && !handedOff.current) {
      handedOff.current = true;
      onRecorded(file);
    }
    if (status === 'recording') {
      handedOff.current = false;
      peakDbfsRef.current = -Infinity;
      everClippedRef.current = false;
    }
  }, [status, file, onRecorded]);

  // Live level meter and readouts, drawn straight to the DOM rather than
  // through React state — this repaints ~60 times a second, and routing that
  // through re-renders would be wasteful for a canvas and its readouts alike.
  useEffect(() => {
    if (!analyser || status !== 'recording') return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const data = new Uint8Array(analyser.frequencyBinCount);
    let raf = 0;

    const draw = () => {
      analyser.getByteTimeDomainData(data);

      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(rect.width * dpr));
      const h = Math.max(1, Math.round(rect.height * dpr));
      if (canvas.width !== w) canvas.width = w;
      if (canvas.height !== h) canvas.height = h;

      // Warm panel ground — the one change to the clear colour; the drawing
      // logic below is otherwise identical to the instrument-skin version.
      ctx.fillStyle = '#1a1512';
      ctx.fillRect(0, 0, w, h);

      const bars = 40;
      const step = Math.max(1, Math.floor(data.length / bars));
      const barWidth = w / bars;

      let framePeak = 0;

      for (let i = 0; i < bars; i++) {
        // Peak deviation from the midline (128) within this bar's slice — a
        // cheap amplitude estimate, plenty for a live meter.
        let peak = 0;
        for (let j = 0; j < step; j++) {
          const sample = data[i * step + j] ?? 128;
          const v = Math.abs(sample - 128);
          if (v > peak) peak = v;
        }
        const amplitude = peak / 128;
        if (amplitude > framePeak) framePeak = amplitude;

        const barHeight = Math.max(h * 0.05, amplitude * h);
        const x = i * barWidth;
        const y = (h - barHeight) / 2;
        const bw = barWidth * 0.56;
        const bx = x + barWidth * 0.22;
        ctx.fillStyle = amplitude > 0.7 ? '#F6E7C4' : '#E0A22B';

        const radius = Math.min(bw / 2, barHeight / 2);
        if (typeof ctx.roundRect === 'function' && radius > 0) {
          ctx.beginPath();
          ctx.roundRect(bx, y, bw, barHeight, radius);
          ctx.fill();
        } else {
          ctx.fillRect(bx, y, bw, barHeight);
        }
      }

      // dBFS from the same peak amplitude the bars are drawn from. The
      // epsilon floor keeps silence from producing -Infinity.
      const levelDbfs = 20 * Math.log10(Math.max(framePeak, 1e-4));
      const clippingNow = framePeak >= CLIP_AMPLITUDE;
      if (clippingNow) everClippedRef.current = true;
      if (levelDbfs > peakDbfsRef.current) peakDbfsRef.current = levelDbfs;

      if (hintRef.current) {
        hintRef.current.textContent = clippingNow
          ? 'A bit loud — try holding the phone further away.'
          : levelDbfs < QUIET_DBFS
            ? 'Very quiet. Move a little closer if you can.'
            : 'Sounding good. Keep going.';
      }
      if (levelValueRef.current) levelValueRef.current.textContent = `${levelDbfs.toFixed(0)} dBFS`;
      if (peakValueRef.current) {
        peakValueRef.current.textContent = `${peakDbfsRef.current.toFixed(0)} dBFS`;
      }
      if (clipValueRef.current) {
        clipValueRef.current.textContent = everClippedRef.current ? 'Detected' : 'None';
      }

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [analyser, status]);

  if (status === 'recording') {
    return (
      <div className="take-live">
        <div className="take-live-head">
          <span className="take-live-who">
            <span className="take-live-lamp" aria-hidden="true" />
            Listening to {subjectLabel}
          </span>
          <span className="take-live-timer">{formatDuration(elapsedMs)}</span>
        </div>

        <div className="scope scope--warm">
          <canvas ref={canvasRef} role="img" aria-label={`Live microphone level for ${subjectLabel}`} />
        </div>

        <p className="take-live-hint" ref={hintRef}>
          Listening…
        </p>

        <div className="take-live-foot">
          <button type="button" className="take-live-stop" onClick={stop}>
            That's it — stop
          </button>

          <button
            type="button"
            className="take-live-toggle"
            aria-expanded={levelsOpen}
            onClick={() => setLevelsOpen((o) => !o)}
          >
            {levelsOpen ? 'Hide levels' : 'Show levels'}
          </button>

          {levelsOpen && (
            <div className="take-live-levels">
              <div>
                <span className="readout-key">Level</span>
                <span className="readout-value" ref={levelValueRef}>
                  —
                </span>
              </div>
              <div>
                <span className="readout-key">Peak</span>
                <span className="readout-value" ref={peakValueRef}>
                  —
                </span>
              </div>
              <div>
                <span className="readout-key">Clipping</span>
                <span className="readout-value" ref={clipValueRef}>
                  —
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="take-idle">
      <p className="take-idle-kicker">Ready when they are</p>

      <div className="take-idle-target">
        <span className="take-idle-halo" aria-hidden="true" />
        <button
          type="button"
          className="take-idle-btn"
          disabled={disabled || status === 'requesting'}
          onClick={() => void start()}
          aria-label="Start recording"
        >
          Listen
        </button>
      </div>

      <p className="take-idle-prompt">Tap when {subjectLabel} starts up.</p>
      <p className="take-idle-note">
        {status === 'requesting'
          ? 'Asking for microphone access…'
          : 'Nothing is kept unless you stop and submit it.'}
      </p>

      <button type="button" className="take-idle-alt" onClick={onSwitchToFile}>
        Upload a file
      </button>

      {error && (
        <p
          className="notice notice--error"
          style={{ marginTop: '0.75rem', marginBottom: 0, textAlign: 'left' }}
        >
          {error.message}
        </p>
      )}

      {standingLabel && <p className="take-idle-standing">{standingLabel}</p>}
    </div>
  );
}
